"use client";

import { useEffect, useState } from "react";
import BlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import { ScriptItemData, ScriptSourceItem } from "@/app/types/messages-content/ScriptData";
import { UScriptSuggestions } from "@/app/utils/UScriptSuggestions";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { buildScriptPlaceholderRows } from "../../Scripts.logic";
import styles from "./ScriptSuggestions.module.css";

type Props = {
  blockType: string;
  title: string;
  isPlaceholder: boolean;
  items: ScriptSourceItem[];
  answers: ScriptItemData[];
  answerPlaceholder: string;
  onOpenQuotes: (insightId: number, label: string) => void;
  onSaveAnswer?: (insightId: number, label: string) => void;
};

export default function ScriptSuggestions(props: Props) {
  const {
    blockType,
    title,
    isPlaceholder,
    items,
    answers,
    answerPlaceholder,
    onOpenQuotes,
    onSaveAnswer,
  } = props;

  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setIsCollapsed(UScriptSuggestions.isCollapsed(blockType));
  }, [blockType]);

  if (isPlaceholder) {
    return (
      <div className={styles.container}>
        <div className={styles.headStatic}>
          <span className={styles.title}>{title}</span>
        </div>
        <BlurredPlaceholder rows={buildScriptPlaceholderRows(3)} className={styles.placeholder} />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.headStatic}>
          <span className={styles.title}>{title}</span>
          <span className={styles.count}>{items.length}</span>
        </div>
      </div>
    );
  }

  const toggle = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    UScriptSuggestions.setCollapsed(blockType, next);
  };

  return (
    <div className={styles.container}>
      <button type="button" className={styles.head} onClick={toggle} aria-expanded={!isCollapsed}>
        <span className={UStyles.classNames(styles.arrow, !isCollapsed && styles.arrowOpen)}>
          <IconArrowDown />
        </span>
        <span className={styles.title}>{title}</span>
        <span className={styles.count}>{items.length}</span>
      </button>

      {!isCollapsed && (
        <ul className={styles.list}>
          {items.map((item) => (
            <li key={item.insightId} className={styles.item}>
              <button
                type="button"
                className={styles.label}
                onClick={() => onOpenQuotes(item.insightId, item.label)}
              >
                {item.label}
              </button>
              <EditableText
                value={
                  answers.find((answer) => answer.sourceInsightId === item.insightId)?.label ?? ""
                }
                onSave={(value) => onSaveAnswer?.(item.insightId, value)}
                placeholder={answerPlaceholder}
                multiline={true}
                maxLength={Limits.MAX_SCRIPT_ITEM_LABEL}
                className={styles.answer}
                textClassName={styles.answerText}
                inputClassName={styles.answerText}
                disabled={!onSaveAnswer}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
